/*
 * Copyright (c) 2021 Pegasystems Inc.
 * All rights reserved.
 */
import services from '../services';
import PubSubUtils from '../utils/pubsub-utils';
import { publicConstants } from '../constants';

import { updateAuthInfo, updateHeaders, getAuthInfo, fetch } from './fetch';
import PegaAuth from './embed/auth';

/**
 * This method returns true if device is connected to network, else returns false
 * @function isOnline
 * @returns true/false
 */

export const isOnline = () => {
  return navigator && navigator.onLine;
};
export const isExternalUrl = (url: string) => {
  return !url?.startsWith(services.getServerURL() || '');
};

export const revokeTokens = async () => {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise<void>(async (resolve, reject) => {
    const authInfo = getAuthInfo();
    if (!authInfo?.tokenInfo) {
      reject(new Error('Missing token information.'));
      return;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { access_token, refresh_token = null } = authInfo.tokenInfo;
    const { revoke: revokeUri } = authInfo.endPoints;
    const { client_id: clientId, client_secret: clientSecret, secureCookie, serverType } = authInfo;
    const bIsLaunchpad = serverType === 'launchpad';

    const headers: { [key: string]: any } = { 'content-type': 'application/x-www-form-urlencoded' };

    if (clientSecret && !bIsLaunchpad) {
      headers.authorization = `Basic ${window.btoa(`${clientId}:${clientSecret}`)}`;
    }

    const formData = new URLSearchParams();
    if (!clientSecret) {
      formData.append('client_id', clientId);
    } else if (bIsLaunchpad) {
      formData.append('client_id', clientId);
      formData.append('client_secret', clientSecret);
    }
    if (secureCookie) {
      formData.append('send_token_as_cookie', 'true');
    }
    // A revoke of a refresh_token will also revoke any associated access_tokens, so only one
    // transaction is necessary.
    const prop = refresh_token ? 'refresh_token' : 'access_token';
    const token = prop === 'access_token' ? access_token : refresh_token;
    if (!secureCookie && token) {
      formData.append('token', token);
    }
    formData.append('token_type_hint', prop);
    // For launchpad public authorization_code grant flow, it will have a empty revokeUri (as not supported)
    if (revokeUri) {
      await fetch(revokeUri, {
        method: 'POST',
        // @ts-expect-error
        headers: new Headers(headers),
        credentials: secureCookie ? 'include' : 'omit',
        body: formData.toString(),
        withoutDefaultHeaders: true
      });
    }
    updateHeaders({ Authorization: null });
    updateAuthInfo({ tokenInfo: null });
    resolve();
  });
};

export const getReAuthenticationURL = async (authInfo: any) => {
  const reauthUrlObj: { [key: string]: any } = {};
  if (authInfo?.authType === 'OAuth2.0') {
    const pegaAuthObj = new PegaAuth(authInfo, false);
    reauthUrlObj.reauthUrl = await pegaAuthObj.buildAuthorizeUrl();
    reauthUrlObj.redirectOrigin = pegaAuthObj.getRedirectUriOrigin();
    window.addEventListener('message', pegaAuthObj.authMessageHandler, false);
  } else {
    reauthUrlObj.reauthUrl =
      services.getServerURL() + (services.getReAuthURL() ? `${services.getReAuthURL()}` : '/?reAuth=true');
  }
  return reauthUrlObj;
};

export const redirectToLogin = (url: string, authInfo: any) => {
  getReAuthenticationURL(authInfo).then((reAuthenticationURLObj) => {
    const props = {
      openReauthMessageModal: true,
      reAuthenticationURL: reAuthenticationURLObj.reauthUrl,
      url,
      redirectOrigin: reAuthenticationURLObj.redirectOrigin
    };
    /* pub sub */
    PubSubUtils.publish(publicConstants.PUB_SUB_EVENTS.EVENT_REAUTH, props);
  });
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const getJWTServerDetails = (authInfo: any): any => {
  let url = '';
  let config = {};
  let callback = {};
  let isRefreshTokenPresent = true;

  if (authInfo?.authType === 'OAuth2.0') {
    url = authInfo.endPoints.token;
    const formData = new URLSearchParams();
    if (
      !authInfo.tokenInfo.refresh_token ||
      (authInfo.tokenInfo.refresh_token && authInfo.tokenInfo.refresh_token.length <= 0)
    ) {
      isRefreshTokenPresent = false;
    }
    formData.append('client_id', authInfo.client_id);
    formData.append('grant_type', 'refresh_token');
    if (authInfo.clientSecret) {
      formData.append('client_secret', authInfo.clientSecret);
    }
    if (authInfo.secureCookie) {
      formData.append('send_token_as_cookie', 'true');
    }
    if (authInfo.tokenInfo?.refresh_token) {
      formData.append('refresh_token', authInfo.tokenInfo.refresh_token);
    }
    config = {
      method: 'POST',
      body: formData.toString(),
      headers: {
        'content-type': 'application/x-www-form-urlencoded'
      }
    };
    callback = {
      success: (response: any) => {
        const { data: updatedtokens } = response;
        const tokens = {
          access_token: updatedtokens?.access_token,
          refresh_token: updatedtokens?.refresh_token
        };
        updateAuthInfo({
          tokenInfo: {
            ...authInfo.tokenInfo,
            ...tokens
          }
        });
        if (authInfo.secureCookie) {
          updateHeaders({
            pzCTkn: updatedtokens?.csrf
          });
        } else {
          updateHeaders({
            Authorization: `${updatedtokens?.token_type} ${updatedtokens?.access_token}`
          });
          // fetchConfig.headers = {
          //   ...fetchConfig.headers,
          //   Authorization: `${updatedtokens?.token_type} ${updatedtokens?.access_token}`
          // };
        }

        const { onTokenRetrieval } = authInfo;
        // call the callback onTokenRetrieval to update pega-embed with new token info
        if (onTokenRetrieval && typeof onTokenRetrieval === 'function') {
          onTokenRetrieval(updatedtokens);
        }
      },

      failure: (portalUrl: string) => {
        if (!authInfo.popupReauth) {
          PubSubUtils.publish(publicConstants.PUB_SUB_EVENTS.EVENT_FULL_REAUTH, authInfo);
        } else {
          // we need to handle this
          return redirectToLogin(portalUrl, authInfo);
        }
      }
    };
  } else {
    url = `${services.getServerURL()}/updateSession`;
    config = {
      method: 'GET',
      headers: {}
    };
    callback = {
      failure: redirectToLogin
    };
  }
  return { url, config, callback, isRefreshTokenPresent };
};

export const isUpdateSessionRequest = (url: string | undefined | null, authInfo: any) => {
  return authInfo?.authType === 'OAuth2.0' ? url?.includes(authInfo.endPoints.token) : url?.includes('updateSession');
};

export const extractConfigFromError = (error: any) => {
  const { config } = error;
  const context = config.headers && config.headers.context;
  if (config.data) {
    config.body = config.data;
  }
  return { url: config.url, config, context };
};

export default {
  getJWTServerDetails,
  getReAuthenticationURL,
  isUpdateSessionRequest,
  redirectToLogin,
  revokeTokens,
  extractConfigFromError
};
